import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    DialogActions,
    DialogContent,
    DialogTitle,
    InputAdornment,
    Popover,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
} from '@mui/material';
import TextField from '@mui/material/TextField';
import SearchIcon from '@mui/icons-material/Search';
import { INITIAL_PAGE_SIZE } from '@pages/testplans/components/TestRunsPopover';
import { getFilterByStringFunction } from '@shared/utils/utilsOld';
import UtmsButton from '@shared/components/ui/UtmsButton';
import { getPaperBackgroundColor } from '@app/theme/themes';
import UtmsSearchTextField from '@shared/components/ui/UtmsSearchTextField';

const PopoverListWithFilter = ({
    anchorEl,
    setAnchorEl,
    data,
    dataFilterFields,
    renderValue,
    table,
    title,
    search = true,
    loadMore,
    setPageSize,
    isLoading,
    Skeleton,
    handleScroll,
    globalFilter,
    setGlobalFilter,
}) => {
    const { t } = useTranslation();
    const open = Boolean(anchorEl);
    const [searchValue, setSearchValue] = useState('');
    const filteredData = useMemo(() => {
        return Array.isArray(data) ? data.filter(getFilterByStringFunction(dataFilterFields, searchValue)) : [];
    }, [data, dataFilterFields, searchValue]);

    useEffect(() => {
        setSearchValue('');
    }, [open]);

    const handleClosePopover = () => {
        setAnchorEl(null);
    };

    const renderItem = useMemo(
        () => (
            <>
                {isLoading && Skeleton && !handleScroll ? <Skeleton /> : filteredData.map(renderValue)}
                {isLoading && handleScroll && Skeleton && <Skeleton />}
                {!filteredData.length && !isLoading && (
                    <Box
                        sx={{
                            fontStyle: 'italic',
                            color: 'var(--text-secondary)',
                            mt: 2,
                        }}
                    >
                        {t('no_items_found')}
                    </Box>
                )}
            </>
        ),
        [Skeleton, filteredData, handleScroll, isLoading, renderValue, t],
    );

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={handleClosePopover}
            anchorOrigin={{
                horizontal: 'left',
                vertical: 'bottom',
            }}
        >
            {title && <DialogTitle variant="h4">{title}</DialogTitle>}
            <DialogContent
                sx={{
                    width: 750,
                    height: 355,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                {search ? (
                    setGlobalFilter && (
                        <UtmsSearchTextField
                            globalFilter={globalFilter}
                            setGlobalFilter={setGlobalFilter}
                            sx={{ mb: 2, mt: '1px' }}
                        />
                    )
                ) : (
                    <TextField
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                        placeholder={t('search') + '...'}
                        fullWidth
                        size="small"
                        autoFocus
                        type="search"
                        sx={{ mb: 2, mt: '1px' }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                )}
                {table ? (
                    <Box sx={{ overflow: 'auto' }} onScroll={handleScroll || undefined}>
                        <Table
                            stickyHeader
                            sx={{
                                '.MuiTableCell-head': {
                                    fontWeight: 700,
                                    backgroundColor: (theme) => getPaperBackgroundColor(11, theme.palette.mode),
                                },
                                '.MuiTableCell-body': {
                                    fontSize: 14,
                                },
                            }}
                        >
                            <TableHead>
                                <TableRow>
                                    {table.columns.map((item) => (
                                        <TableCell key={item} sx={{ fontSize: 14 }} size="small">
                                            {item}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>{renderItem}</TableBody>
                        </Table>
                    </Box>
                ) : (
                    <Box
                        sx={{
                            padding: '0 8px 8px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '4px',
                            width: '280px',
                            maxHeight: '200px',
                            overflow: 'auto',
                        }}
                    >
                        {renderItem}
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                {loadMore && setPageSize && (
                    <UtmsButton onClick={() => setPageSize((prev) => prev + INITIAL_PAGE_SIZE)}>
                        {t('load_more')}...
                    </UtmsButton>
                )}
                <UtmsButton onClick={handleClosePopover}>{t('close')}</UtmsButton>
            </DialogActions>
        </Popover>
    );
};

export default PopoverListWithFilter;
